import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Grid,
  Button,
  Divider,
} from '@mui/material';

import {
  TrendingUp,
  TrendingDown,
  Schedule,
  People,
  Info,
  Warning,
  CheckCircle,
  FilterList,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Scatter,
  ScatterChart,
  ZAxis,
} from 'recharts';
import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import suburbsData from '../../data/suburbs.json';

// Type definition for suburbs data
interface SuburbEntry {
  suburb: string;
  postcode: number | string;
  state?: string;
}

interface SuburbsDataStructure {
  data: SuburbEntry[];
}

// Build postcode to suburb lookup map from JSON data
const postcodeMap = new Map<string, string>();
const typedSuburbsData = suburbsData as SuburbsDataStructure;

typedSuburbsData.data.forEach((entry: SuburbEntry) => {
  const postcode = String(entry.postcode);
  // Use the first suburb found for each postcode (some postcodes have multiple suburbs)
  if (!postcodeMap.has(postcode)) {
    postcodeMap.set(postcode, entry.suburb);
  }
});

console.log(`Loaded ${postcodeMap.size} postcodes from suburbs.json`);

// Shared chart styling
const GRID_STYLE = { stroke: '#eef0f5', strokeDasharray: '4 4' };
const AXIS_STYLE = { tick: { fill: '#8a94a6', fontSize: 11 }, axisLine: { stroke: '#e8eaf0' }, tickLine: false };
const TOOLTIP_STYLE = {
  contentStyle: { background: '#fff', border: '1px solid #e8eaf0', borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', fontSize: 12, padding: '10px 14px' },
  labelStyle: { fontWeight: 600, color: '#1a1d23', marginBottom: 4 },
};
const TOGGLE_STYLES = {
  '& .MuiToggleButton-root': {
    border: '1px solid #e0e3ea', px: 1.75, py: 0.5,
    fontSize: '0.78rem', fontWeight: 500, color: '#8a94a6', textTransform: 'none',
    '&.Mui-selected': { background: '#6c63ff', color: '#fff', borderColor: '#6c63ff', '&:hover': { background: '#5b52e0' } },
  },
};

// Simple suburb lookup using the loaded data
const getSuburbFromPostcode = (postcode: string): string | null => {
  return postcodeMap.get(postcode) || null;
};

interface BestTimeCache {
  id: string;
  venue_id: string;
  cached_at: Timestamp;
  expires_at: Timestamp;
  hour_key: string;
  current_popularity: number;
  busyness_data: {
    status: string;
    analysis: {
      venue_forecasted_busyness: number | null;
      venue_live_busyness: number | null;
      venue_live_forecasted_delta: number | null;
      venue_forecast_busyness_available: boolean;
      venue_live_busyness_available: boolean;
      hour_start: number;
      hour_end: number;
      hour_start_12: string;
      hour_end_12: string;
    };
    venue_info: {
      venue_name: string;
      venue_address: string;
      venue_current_localtime: string;
      rating?: number;
      reviews?: number;
      venue_open?: string;
    };
  };
}

interface BusynessDataPoint {
  timestamp: string;
  time: string;
  venue_name: string;
  forecasted: number | null;
  live: number | null;
  delta: number | null;
  hasLive: boolean;
  hasForecast: boolean;
}

interface VenueBusynessStats {
  venue_name: string;
  venue_id: string;
  total_checks: number;
  avg_forecasted: number;
  avg_live: number;
  avg_delta: number;
  accuracy_rate: number; // How close forecasts are to live data
  data_points: BusynessDataPoint[];
}

type ChartType = 'line' | 'area' | 'comparison' | 'scatter';
type TimeRange = 'day' | 'week' | 'month' | 'all' | 'custom';

const BestTimeAnalytics: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [chartType, setChartType] = useState<ChartType>('comparison');
  const [timeRange, setTimeRange] = useState<TimeRange>('week');
  const [selectedVenue, setSelectedVenue] = useState<string>('all');
  const [selectedState, setSelectedState] = useState<string>('all');
  const [selectedSuburb, setSelectedSuburb] = useState<string>('all');
  const [isAtDataLimit, setIsAtDataLimit] = useState<boolean>(false);
  
  // Custom date range
  const [customStart, setCustomStart] = useState<Dayjs | null>(null);
  const [customEnd, setCustomEnd] = useState<Dayjs | null>(null);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  // Period comparison
  const [compareModeActive, setCompareModeActive] = useState(false);
  const [compareStartA, setCompareStartA] = useState<Dayjs | null>(null);
  const [compareEndA, setCompareEndA] = useState<Dayjs | null>(null);
  const [compareStartB, setCompareStartB] = useState<Dayjs | null>(null);
  const [compareEndB, setCompareEndB] = useState<Dayjs | null>(null);
  const [compareDataA, setCompareDataA] = useState<BusynessDataPoint[]>([]);
  const [compareDataB, setCompareDataB] = useState<BusynessDataPoint[]>([]);
  const [compareLoading, setCompareLoading] = useState(false);
  const [allData, setAllData] = useState<BusynessDataPoint[]>([]);
  const [venueStats, setVenueStats] = useState<VenueBusynessStats[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [suburbs, setSuburbs] = useState<string[]>([]);
  const [venues, setVenues] = useState<{ id: string; name: string; address: string; state: string; postcode: string; suburb: string }[]>([]);
  const [aggregateStats, setAggregateStats] = useState({
    total_entries: 0,
    entries_with_forecast: 0,
    entries_with_live: 0,
    entries_with_both: 0,
    avg_forecast_accuracy: 0,
    avg_forecasted_busyness: 0,
    avg_live_busyness: 0,
  });

  // ✅ ADDED: Loading ref to prevent double loads
  const isLoadingRef = React.useRef(false);

  // Helper function to extract state, postcode, and suburb from address
  const parseAddress = (address: string): { state: string; postcode: string; suburb: string } => {
    // Example: "1D Ponderosa Parade Warriewood NSW 2103 Australia"
    // Strategy: Find 4-digit postcode, state is immediately to the left
    
    if (!address) {
      return { state: 'Unknown', postcode: 'Unknown', suburb: 'Unknown' };
    }

    // Match 4-digit postcode (Australian postcodes are always 4 digits)
    const postcodePattern = /\b(\d{4})\b/;
    const postcodeMatch = address.match(postcodePattern);
    
    if (!postcodeMatch) {
      console.warn('⚠️ No postcode found in address:', address);
      return { state: 'Unknown', postcode: 'Unknown', suburb: 'Unknown' };
    }
    
    const postcode = postcodeMatch[1];
    const postcodeIndex = postcodeMatch.index!;
    
    // Get text before postcode
    const beforePostcode = address.substring(0, postcodeIndex).trim();
    
    // State code should be the last word before postcode
    const words = beforePostcode.split(/\s+/);
    const lastWord = words[words.length - 1];
    
    // Check if it's a valid Australian state
    const statePattern = /^(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)$/i;
    const state = statePattern.test(lastWord) ? lastWord.toUpperCase() : 'Unknown';
    
    if (state === 'Unknown') {
      console.warn('⚠️ No valid state found before postcode in address:', address);
    }
    
    // Lookup suburb from postcode using local lookup
    let suburb = 'Unknown';
    try {
      suburb = getSuburbFromPostcode(postcode) || 'Unknown';
    } catch (error) {
      console.warn('⚠️ Could not find suburb for postcode:', postcode);
    }
    
    return { state, postcode, suburb };
  };

  // ✅ FIXED: Only ONE useEffect to load data when timeRange changes
  useEffect(() => {
    // Don't auto-load for custom range — wait for Apply button
    if (timeRange === 'custom') return;

    // Prevent double loading in React Strict Mode
    if (isLoadingRef.current) {
      console.log('⏭️ Skipping duplicate load');
      return;
    }
    
    isLoadingRef.current = true;
    loadBestTimeData().finally(() => {
      isLoadingRef.current = false;
    });
  }, [timeRange]);

  const loadBestTimeData = async () => {
    console.log('🔄 loadBestTimeData called at', new Date().toISOString());
    
    try {
      setLoading(true);
      setError('');

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      
      if (timeRange === 'custom' && customStart && customEnd) {
        startDate.setTime(customStart.toDate().getTime());
        endDate.setTime(customEnd.toDate().getTime());
      } else {
        switch (timeRange) {
          case 'day':   startDate.setDate(startDate.getDate() - 1); break;
          case 'week':  startDate.setDate(startDate.getDate() - 7); break;
          case 'month': startDate.setDate(startDate.getDate() - 30); break;
          case 'all':   startDate.setFullYear(2025, 0, 1); break;
        }
      }

      // Query besttime_cache collection
      // For 'all' time range, skip the date filter so we get true history
      const cacheQuery = timeRange === 'all'
        ? query(
            collection(db, 'besttime_cache'),
            orderBy('cached_at', 'asc'),
            limit(2000) // Capped for memory — warn user if at limit
          )
        : query(
            collection(db, 'besttime_cache'),
            where('cached_at', '>=', Timestamp.fromDate(startDate)),
            orderBy('cached_at', 'asc'),
            limit(1000)
          );

      const snapshot = await getDocs(cacheQuery);
      const cacheData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          venue_id: data.venue_id || '',
          cached_at: data.cached_at,
          expires_at: data.expires_at,
          hour_key: data.hour_key || '',
          current_popularity: data.current_popularity || 0,
          busyness_data: data.busyness_data || {},
        } as BestTimeCache;
      });

      const DATA_LIMIT = timeRange === 'all' ? 2000 : 1000;
      const hitLimit = cacheData.length >= DATA_LIMIT;
      setIsAtDataLimit(hitLimit);
      console.log(`📊 Loaded ${cacheData.length} besttime_cache entries${hitLimit ? ' (AT LIMIT)' : ''}`);

      // Debug: Log first entry to see structure
      if (cacheData.length > 0) {
        console.log('🔍 Sample cache entry:', cacheData[0]);
        console.log('🔍 Busyness data:', cacheData[0].busyness_data);
      }

      // Process data into chart-friendly format
      const dataPoints: BusynessDataPoint[] = cacheData
        .filter(entry => {
          const hasStatus = entry.busyness_data?.status === 'OK';
          if (!hasStatus) {
            console.warn('⚠️ Entry missing OK status:', entry.id);
          }
          return hasStatus;
        })
        .map(entry => {
          const analysis = entry.busyness_data?.analysis;
          const venue_info = entry.busyness_data?.venue_info;
          
          if (!analysis) {
            console.warn('⚠️ Entry missing analysis:', entry.id);
          }
          if (!venue_info) {
            console.warn('⚠️ Entry missing venue_info:', entry.id);
          }
          
          return {
            timestamp: entry.cached_at.toDate().toISOString(),
            time: entry.cached_at.toDate().toLocaleString('en-US', { 
              month: 'short', 
              day: 'numeric', 
              hour: 'numeric',
              minute: '2-digit'
            }),
            venue_name: venue_info?.venue_name || 'Unknown Venue',
            forecasted: analysis?.venue_forecast_busyness_available ? analysis.venue_forecasted_busyness : null,
            live: analysis?.venue_live_busyness_available ? analysis.venue_live_busyness : null,
            delta: analysis?.venue_live_forecasted_delta || null,
            hasLive: analysis?.venue_live_busyness_available || false,
            hasForecast: analysis?.venue_forecast_busyness_available || false,
          };
        })
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      console.log(`📈 Processed ${dataPoints.length} data points`);
      console.log('📈 Sample data point:', dataPoints[0]);
      console.log(`📊 Points with forecast: ${dataPoints.filter(p => p.hasForecast).length}`);
      console.log(`📊 Points with live: ${dataPoints.filter(p => p.hasLive).length}`);

      setAllData(dataPoints);

      // Helper function to normalize strings for better deduplication
      const normalizeString = (str: string): string => {
        return str.toLowerCase().trim().replace(/\s+/g, ' ');
      };

      // Extract unique venues with their location data
      const venueLocationMap = new Map<string, { name: string; address: string; state: string; postcode: string; suburb: string }>();
      cacheData.forEach(entry => {
        const venue_info = entry.busyness_data?.venue_info;
        if (venue_info?.venue_name && venue_info?.venue_address) {
          const { state, postcode, suburb } = parseAddress(venue_info.venue_address);
          
          // ✅ FIXED: Use normalized name + postcode as unique key
          const uniqueKey = `${normalizeString(venue_info.venue_name)}_${postcode}`;
          
          // Only add if we haven't seen this venue+postcode combination before
          if (!venueLocationMap.has(uniqueKey)) {
            // Debug: Log first 3 parsed addresses
            if (venueLocationMap.size < 3) {
              console.log('📍 Parsed address:', {
                original: venue_info.venue_address,
                venue: venue_info.venue_name,
                state,
                postcode,
                suburb,
                uniqueKey
              });
            }
            
            venueLocationMap.set(uniqueKey, {
              name: venue_info.venue_name,
              address: venue_info.venue_address,
              state,
              postcode,
              suburb,
            });
          }
        }
      });

      const venueList = Array.from(venueLocationMap.values()).map((v) => ({
        id: `${normalizeString(v.name)}_${v.postcode}`, // Use consistent ID without index
        name: v.name,
        address: v.address,
        state: v.state,
        postcode: v.postcode,
        suburb: v.suburb,
      }));

      // Extract unique states and suburbs
      const uniqueStates = Array.from(new Set(venueList.map(v => v.state))).filter(s => s !== 'Unknown').sort();
      const uniqueSuburbs = Array.from(new Set(venueList.map(v => v.suburb))).filter(s => s !== 'Unknown').sort();

      // ✅ FINAL DEDUPLICATION: Ensure absolutely no duplicates based on ID
      const uniqueVenues = Array.from(
        new Map(venueList.map(v => [v.id, v])).values()
      );

      console.log(`📍 Venue list before dedup: ${venueList.length}, after: ${uniqueVenues.length}`);

      setVenues(uniqueVenues);
      setStates(uniqueStates);
      setSuburbs(uniqueSuburbs);

      console.log(`📍 Found ${uniqueStates.length} states:`, uniqueStates);
      console.log(`📍 Found ${uniqueSuburbs.length} suburbs:`, uniqueSuburbs);
      console.log(`📍 Setting ${uniqueVenues.length} unique venues to state`);
      
      // Debug: Check for duplicates in venueList
      const duplicateCheck = new Map<string, number>();
      venueList.forEach(v => {
        const key = `${v.name}_${v.postcode}`;
        duplicateCheck.set(key, (duplicateCheck.get(key) || 0) + 1);
      });
      const duplicates = Array.from(duplicateCheck.entries()).filter(([_, count]) => count > 1);
      if (duplicates.length > 0) {
        console.error('❌ DUPLICATE VENUES DETECTED:', duplicates);
      } else {
        console.log('✅ No duplicate venues in venueList');
      }
      
      // Debug: Show a few venue examples
      if (venueList.length > 0) {
        console.log('📍 Sample venues with location:', venueList.slice(0, 3).map(v => ({
          id: v.id,
          name: v.name,
          suburb: v.suburb,
          postcode: v.postcode,
          state: v.state
        })));
      }

      // Calculate venue-specific statistics
      const venueMap = new Map<string, BusynessDataPoint[]>();
      dataPoints.forEach(point => {
        if (!venueMap.has(point.venue_name)) {
          venueMap.set(point.venue_name, []);
        }
        venueMap.get(point.venue_name)!.push(point);
      });

      const venueStatistics: VenueBusynessStats[] = Array.from(venueMap.entries()).map(([venue_name, points]) => {
        const pointsWithBoth = points.filter(p => p.hasForecast && p.hasLive);
        const totalDelta = pointsWithBoth.reduce((sum, p) => sum + Math.abs(p.delta || 0), 0);
        const avgDelta = pointsWithBoth.length > 0 ? totalDelta / pointsWithBoth.length : 0;
        
        // Accuracy rate: 100% - average percentage difference
        const accuracy = pointsWithBoth.length > 0 
          ? 100 - (avgDelta / pointsWithBoth.length) 
          : 0;

        return {
          venue_name,
          venue_id: venue_name,
          total_checks: points.length,
          avg_forecasted: points.filter(p => p.forecasted !== null).reduce((sum, p) => sum + (p.forecasted || 0), 0) / points.filter(p => p.forecasted !== null).length || 0,
          avg_live: points.filter(p => p.live !== null).reduce((sum, p) => sum + (p.live || 0), 0) / points.filter(p => p.live !== null).length || 0,
          avg_delta: avgDelta,
          accuracy_rate: Math.max(0, accuracy),
          data_points: points,
        };
      });

      setVenueStats(venueStatistics);

      // Calculate aggregate statistics
      const totalEntries = dataPoints.length;
      const entriesWithForecast = dataPoints.filter(p => p.hasForecast).length;
      const entriesWithLive = dataPoints.filter(p => p.hasLive).length;
      const entriesWithBoth = dataPoints.filter(p => p.hasForecast && p.hasLive).length;
      
      const avgForecasted = dataPoints
        .filter(p => p.forecasted !== null)
        .reduce((sum, p) => sum + (p.forecasted || 0), 0) / entriesWithForecast || 0;
      
      const avgLive = dataPoints
        .filter(p => p.live !== null)
        .reduce((sum, p) => sum + (p.live || 0), 0) / entriesWithLive || 0;
      
      const avgAccuracy = venueStatistics.reduce((sum, v) => sum + v.accuracy_rate, 0) / venueStatistics.length || 0;

      setAggregateStats({
        total_entries: totalEntries,
        entries_with_forecast: entriesWithForecast,
        entries_with_live: entriesWithLive,
        entries_with_both: entriesWithBoth,
        avg_forecast_accuracy: avgAccuracy,
        avg_forecasted_busyness: avgForecasted,
        avg_live_busyness: avgLive,
      });

    } catch (err) {
      console.error('Error loading besttime data:', err);
      setError('Failed to load busyness data');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    let filtered = allData;

    // Filter by state
    if (selectedState !== 'all') {
      const venueNamesInState = venues.filter(v => v.state === selectedState).map(v => v.name);
      filtered = filtered.filter(d => venueNamesInState.includes(d.venue_name));
    }

    // Filter by suburb
    if (selectedSuburb !== 'all') {
      const venueNamesInSuburb = venues.filter(v => v.suburb === selectedSuburb).map(v => v.name);
      filtered = filtered.filter(d => venueNamesInSuburb.includes(d.venue_name));
    }

    // Filter by specific venue
    if (selectedVenue !== 'all') {
      filtered = filtered.filter(d => d.venue_name === selectedVenue);
    }

    return filtered;
  };

  const filteredData = getFilteredData();

  // Get available options based on current filters
  const getAvailableSuburbs = () => {
    if (selectedState === 'all') {
      return suburbs;
    }
    return Array.from(new Set(venues.filter(v => v.state === selectedState).map(v => v.suburb))).sort();
  };

  const getAvailableVenues = () => {
    let filtered = venues;
    
    if (selectedState !== 'all') {
      filtered = filtered.filter(v => v.state === selectedState);
    }
    
    if (selectedSuburb !== 'all') {
      filtered = filtered.filter(v => v.suburb === selectedSuburb);
    }
    
    // ✅ EXTRA SAFETY: Remove any duplicates based on unique ID
    const seen = new Set<string>();
    const deduplicated = filtered.filter(v => {
      if (seen.has(v.id)) {
        console.warn('⚠️ Duplicate venue detected:', v.name, v.postcode);
        return false;
      }
      seen.add(v.id);
      return true;
    });
    
    console.log(`📍 getAvailableVenues: ${filtered.length} venues (${deduplicated.length} after deduplication)`);
    
    return deduplicated;
  };

  const renderChart = (): React.ReactElement => {
    const commonXAxis = (
      <XAxis dataKey="time" {...AXIS_STYLE} angle={-35} textAnchor="end" height={80} interval="preserveStartEnd" />
    );
    const commonYAxis = (
      <YAxis {...AXIS_STYLE} width={42} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
    );

    // Period comparison overlay chart
    if (compareModeActive && compareDataA.length > 0 && compareDataB.length > 0) {
      const compareChartData = buildCompareChartData();
      return (
        <LineChart data={compareChartData}>
          <CartesianGrid {...GRID_STYLE} vertical={false} />
          <XAxis dataKey="hour" {...AXIS_STYLE} label={{ value: 'Hour of day', position: 'insideBottom', offset: -10, fontSize: 11 }} />
          <YAxis {...AXIS_STYLE} width={42} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
          <RechartsTooltip content={({ active, payload, label }) => {
            if (active && payload && payload.length) {
              const d = compareChartData.find(c => c.hour === label);
              if (!d) return null;
              return (
                <Box sx={{ background: '#fff', border: '1px solid #e8eaf0', borderRadius: 2, p: 1.75, fontSize: 12 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#6c63ff', mb: 0.5 }}>Period A — {d.timeA}</Typography>
                  {d.forecastA != null && <Typography sx={{ fontSize: '0.75rem', color: '#6c63ff' }}>Forecast: {d.forecastA}%</Typography>}
                  {d.liveA != null && <Typography sx={{ fontSize: '0.75rem', color: '#00BCD4' }}>Live: {d.liveA}%</Typography>}
                  <Divider sx={{ my: 0.75 }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#F24F2F', mb: 0.5 }}>Period B — {d.timeB}</Typography>
                  {d.forecastB != null && <Typography sx={{ fontSize: '0.75rem', color: '#F24F2F' }}>Forecast: {d.forecastB}%</Typography>}
                  {d.liveB != null && <Typography sx={{ fontSize: '0.75rem', color: '#FF9800' }}>Live: {d.liveB}%</Typography>}
                </Box>
              );
            }
            return null;
          }} />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          <Line type="monotone" dataKey="forecastA" stroke="#6c63ff" strokeWidth={2.5} dot={false} name="Period A — Forecast" connectNulls />
          <Line type="monotone" dataKey="liveA" stroke="#00BCD4" strokeWidth={2.5} strokeDasharray="5 3" dot={false} name="Period A — Live" connectNulls />
          <Line type="monotone" dataKey="forecastB" stroke="#F24F2F" strokeWidth={2.5} dot={false} name="Period B — Forecast" connectNulls />
          <Line type="monotone" dataKey="liveB" stroke="#FF9800" strokeWidth={2.5} strokeDasharray="5 3" dot={false} name="Period B — Live" connectNulls />
          <ReferenceLine y={50} stroke="#e8eaf0" strokeDasharray="4 4" />
        </LineChart>
      );
    }

    if (chartType === 'comparison') {
      return (
        <ComposedChart data={filteredData}>
          <defs>
            <linearGradient id="bForecast" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6c63ff" stopOpacity={0.18}/>
              <stop offset="100%" stopColor="#6c63ff" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID_STYLE} vertical={false} />
          {commonXAxis}
          {commonYAxis}
          <RechartsTooltip {...TOOLTIP_STYLE} content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const d = payload[0].payload;
              return (
                <Box sx={{ background: '#fff', border: '1px solid #e8eaf0', borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', p: 1.75, fontSize: 12 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1d23', mb: 0.5 }}>{d.venue_name}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#8a94a6', mb: 1 }}>{d.time}</Typography>
                  {d.forecasted !== null && <Typography sx={{ fontSize: '0.78rem', color: '#6c63ff' }}>Forecasted: {d.forecasted}%</Typography>}
                  {d.live !== null && <Typography sx={{ fontSize: '0.78rem', color: '#16a76a' }}>Live: {d.live}%</Typography>}
                  {d.delta !== null && <Typography sx={{ fontSize: '0.78rem', color: d.delta > 0 ? '#f59e0b' : '#14b8a6' }}>Delta: {d.delta > 0 ? '+' : ''}{d.delta}%</Typography>}
                </Box>
              );
            }
            return null;
          }} />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          <Bar dataKey="forecasted" fill="#6c63ff" fillOpacity={0.5} name="Forecasted" radius={[2, 2, 0, 0]} />
          <Line type="monotone" dataKey="live" stroke="#16a76a" strokeWidth={2.5} name="Live" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} connectNulls />
          <ReferenceLine y={50} stroke="#e8eaf0" strokeDasharray="4 4" label={{ value: '50%', fill: '#8a94a6', fontSize: 11 }} />
        </ComposedChart>
      );
    }

    if (chartType === 'line') {
      return (
        <LineChart data={filteredData}>
          <CartesianGrid {...GRID_STYLE} vertical={false} />
          {commonXAxis}
          {commonYAxis}
          <RechartsTooltip {...TOOLTIP_STYLE} />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          <Line type="monotone" dataKey="forecasted" stroke="#6c63ff" strokeWidth={2} name="Forecasted" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} connectNulls />
          <Line type="monotone" dataKey="live" stroke="#16a76a" strokeWidth={2} name="Live" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} connectNulls />
        </LineChart>
      );
    }

    if (chartType === 'area') {
      return (
        <AreaChart data={filteredData}>
          <defs>
            <linearGradient id="gForecast" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6c63ff" stopOpacity={0.15}/><stop offset="100%" stopColor="#6c63ff" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="gLive" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#16a76a" stopOpacity={0.15}/><stop offset="100%" stopColor="#16a76a" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid {...GRID_STYLE} vertical={false} />
          {commonXAxis}
          {commonYAxis}
          <RechartsTooltip {...TOOLTIP_STYLE} />
          <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
          <Area type="monotone" dataKey="forecasted" stroke="#6c63ff" strokeWidth={2} fill="url(#gForecast)" name="Forecasted" dot={false} connectNulls />
          <Area type="monotone" dataKey="live" stroke="#16a76a" strokeWidth={2} fill="url(#gLive)" name="Live" dot={false} connectNulls />
        </AreaChart>
      );
    }

    if (chartType === 'scatter') {
      return (
        <ScatterChart>
          <CartesianGrid {...GRID_STYLE} />
          <XAxis type="number" dataKey="forecasted" name="Forecasted" domain={[0, 100]} {...AXIS_STYLE} label={{ value: 'Forecasted (%)', position: 'bottom', fill: '#8a94a6', fontSize: 11 }} />
          <YAxis type="number" dataKey="live" name="Live" domain={[0, 100]} {...AXIS_STYLE} width={42} label={{ value: 'Live (%)', angle: -90, position: 'insideLeft', fill: '#8a94a6', fontSize: 11 }} />
          <ZAxis range={[40, 160]} />
          <RechartsTooltip {...TOOLTIP_STYLE} content={({ active, payload }) => {
            if (active && payload && payload.length) {
              const d = payload[0].payload;
              return (
                <Box sx={{ background: '#fff', border: '1px solid #e8eaf0', borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', p: 1.75, fontSize: 12 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1d23', mb: 0.5 }}>{d.venue_name}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#8a94a6', mb: 0.5 }}>{d.time}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#6c63ff' }}>Forecasted: {d.forecasted}%</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#16a76a' }}>Live: {d.live}%</Typography>
                </Box>
              );
            }
            return null;
          }} />
          <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 100, y: 100 }]} stroke="#e8eaf0" strokeDasharray="4 4" label={{ value: 'Perfect', fill: '#8a94a6', fontSize: 11 }} />
          <Scatter name="Forecast vs Live" data={filteredData.filter(d => d.forecasted !== null && d.live !== null)} fill="#6c63ff" fillOpacity={0.55} />
        </ScatterChart>
      );
    }

    // Default fallback
    return (
      <ComposedChart data={filteredData}>
        <CartesianGrid {...GRID_STYLE} vertical={false} />
        {commonXAxis}
        {commonYAxis}
        <RechartsTooltip {...TOOLTIP_STYLE} />
        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
        <Bar dataKey="forecasted" fill="#6c63ff" fillOpacity={0.5} name="Forecasted" radius={[2, 2, 0, 0]} />
        <Line type="monotone" dataKey="live" stroke="#16a76a" strokeWidth={2.5} name="Live" dot={false} connectNulls />
        <ReferenceLine y={50} stroke="#e8eaf0" strokeDasharray="4 4" />
      </ComposedChart>
    );
  };

  const handleChartTypeChange = (event: React.MouseEvent<HTMLElement>, newType: ChartType | null) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  const handleTimeRangeChange = (event: React.MouseEvent<HTMLElement>, newRange: TimeRange | null) => {
    if (newRange !== null) {
      setTimeRange(newRange);
      setShowCustomPicker(newRange === 'custom');
      if (newRange !== 'custom') { setCustomStart(null); setCustomEnd(null); }
    }
  };

  const handleApplyCustomRange = () => {
    if (customStart && customEnd) loadBestTimeData();
  };

  const fetchPeriodData = async (start: Dayjs, end: Dayjs): Promise<BusynessDataPoint[]> => {
    // Set end to end of day so we capture all data for that day
    const endOfDay = end.endOf('day');
    const snap = await getDocs(query(
      collection(db, 'besttime_cache'),
      where('cached_at', '>=', Timestamp.fromDate(start.startOf('day').toDate())),
      where('cached_at', '<=', Timestamp.fromDate(endOfDay.toDate())),
      orderBy('cached_at', 'asc'),
      limit(500) // Tighter limit since max 24hrs per period
    ));
    return snap.docs.map(doc => {
      const data = doc.data();
      const analysis = data.busyness_data?.analysis;
      const venue_info = data.busyness_data?.venue_info;
      const ts = data.cached_at?.toDate?.();
      return {
        id: doc.id,
        venue_name: venue_info?.venue_name || data.venue_id || 'Unknown',
        venue_id: data.venue_id || '',
        time: ts ? ts.toLocaleDateString('en-AU', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '',
        timestamp: ts?.getTime() || 0,
        forecasted: analysis?.venue_forecasted_busyness ?? null,
        live: analysis?.venue_live_busyness ?? null,
        delta: analysis?.venue_live_forecasted_delta ?? null,
        hasForecast: analysis?.venue_forecast_busyness_available === true,
        hasLive: analysis?.venue_live_busyness_available === true,
        suburb: venue_info?.venue_address?.match(/([A-Za-z ]+)\s+(?:NSW|VIC|QLD|SA|WA|TAS|NT|ACT)/)?.[1]?.trim() || '',
        state: venue_info?.venue_address?.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/)?.[1] || '',
      } as BusynessDataPoint;
    }).filter(d => d.time !== '');
  };

  const handleRunComparison = async () => {
    if (!compareStartA || !compareEndA || !compareStartB || !compareEndB) return;

    // Enforce 24-hour max per period
    const diffA = compareEndA.diff(compareStartA, 'hour');
    const diffB = compareEndB.diff(compareStartB, 'hour');
    if (diffA > 24 || diffB > 24) {
      alert('Each comparison period must be 24 hours or less for performance. Please narrow your date range.');
      return;
    }

    setCompareLoading(true);
    try {
      const [dataA, dataB] = await Promise.all([
        fetchPeriodData(compareStartA, compareEndA),
        fetchPeriodData(compareStartB, compareEndB),
      ]);

      // Apply same location filters as main chart
      const applyFilters = (data: BusynessDataPoint[]) => {
        let filtered = data;
        if (selectedState !== 'all') {
          const namesInState = venues.filter(v => v.state === selectedState).map(v => v.name);
          filtered = filtered.filter(d => namesInState.includes(d.venue_name));
        }
        if (selectedSuburb !== 'all') {
          const namesInSuburb = venues.filter(v => v.suburb === selectedSuburb).map(v => v.name);
          filtered = filtered.filter(d => namesInSuburb.includes(d.venue_name));
        }
        if (selectedVenue !== 'all') {
          filtered = filtered.filter(d => d.venue_name === selectedVenue);
        }
        return filtered;
      };

      setCompareDataA(applyFilters(dataA));
      setCompareDataB(applyFilters(dataB));
    } catch (e) {
      console.error('Comparison fetch error:', e);
    } finally {
      setCompareLoading(false);
    }
  };

  // Build comparison chart data — normalise both periods to relative time index
  const buildCompareChartData = () => {
    // Build a map keyed by hour-of-day (0-23) for each period
    const hourMapA = new Map<number, BusynessDataPoint>();
    const hourMapB = new Map<number, BusynessDataPoint>();

    compareDataA.forEach(p => {
      const hour = new Date(p.timestamp).getHours();
      if (!hourMapA.has(hour)) hourMapA.set(hour, p);
    });
    compareDataB.forEach(p => {
      const hour = new Date(p.timestamp).getHours();
      if (!hourMapB.has(hour)) hourMapB.set(hour, p);
    });

    return Array.from({ length: 24 }, (_, hour) => ({
      hour: `${hour}:00`,
      forecastA: hourMapA.get(hour)?.forecasted ?? null,
      liveA: hourMapA.get(hour)?.live ?? null,
      forecastB: hourMapB.get(hour)?.forecasted ?? null,
      liveB: hourMapB.get(hour)?.live ?? null,
      timeA: hourMapA.get(hour)?.time || '',
      timeB: hourMapB.get(hour)?.time || '',
    })).filter(d => d.forecastA !== null || d.forecastB !== null);
  };

  const handleStateChange = (event: SelectChangeEvent) => {
    const newState = event.target.value;
    setSelectedState(newState);
    
    // Reset suburb and venue when state changes
    setSelectedSuburb('all');
    setSelectedVenue('all');
  };

  const handleSuburbChange = (event: SelectChangeEvent) => {
    const newSuburb = event.target.value;
    setSelectedSuburb(newSuburb);
    
    // Reset venue when suburb changes
    setSelectedVenue('all');
  };

  const handleVenueChange = (event: SelectChangeEvent) => {
    setSelectedVenue(event.target.value);
  };

  if (loading) {
    return (
      <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <CircularProgress size={40} sx={{ color: '#6c63ff' }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ background: '#f8f9fc' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1a1d23">Venue Capacity Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Forecasted vs live capacity comparison from JamApp data</Typography>
        </Box>
        <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
          <ToggleButtonGroup value={timeRange} exclusive onChange={handleTimeRangeChange} size="small" sx={TOGGLE_STYLES}>
            <ToggleButton value="day">24H</ToggleButton>
            <ToggleButton value="week">7 Days</ToggleButton>
            <ToggleButton value="month">30 Days</ToggleButton>
            <ToggleButton value="all">All Time</ToggleButton>
            <ToggleButton value="custom">Custom</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup value={chartType} exclusive onChange={handleChartTypeChange} size="small" sx={TOGGLE_STYLES}>
            <ToggleButton value="comparison">Compare</ToggleButton>
            <ToggleButton value="line">Lines</ToggleButton>
            <ToggleButton value="area">Area</ToggleButton>
            <ToggleButton value="scatter">Scatter</ToggleButton>
          </ToggleButtonGroup>
          <Button
            size="small"
            variant={compareModeActive ? 'contained' : 'outlined'}
            onClick={() => setCompareModeActive(v => !v)}
            sx={{ height: 32, fontSize: '0.75rem', fontWeight: 600, borderRadius: 1,
              ...(compareModeActive ? { bgcolor: '#F24F2F', borderColor: '#F24F2F', '&:hover': { bgcolor: '#d43f22' } } : { borderColor: '#e8eaf0', color: '#1a1d23' }) }}
          >
            Compare Periods
          </Button>
        </Box>
      </Box>

      {/* Custom date range picker */}
      {showCustomPicker && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box display="flex" gap={2} alignItems="center" mb={2} flexWrap="wrap"
            sx={{ p: 2, borderRadius: 2, background: '#f8f9fc', border: '1px solid #e8eaf0' }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mr: 1 }}>CUSTOM RANGE</Typography>
            <DatePicker label="From" value={customStart} onChange={setCustomStart}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }} />
            <DatePicker label="To" value={customEnd} onChange={setCustomEnd}
              minDate={customStart || undefined}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 150 } } }} />
            <Button variant="contained" size="small" onClick={handleApplyCustomRange}
              disabled={!customStart || !customEnd}
              sx={{ height: 40, bgcolor: '#6c63ff', '&:hover': { bgcolor: '#5a52d5' } }}>
              Apply
            </Button>
          </Box>
        </LocalizationProvider>
      )}

      {/* Period comparison pickers */}
      {compareModeActive && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ p: 2.5, mb: 2, borderRadius: 2, background: '#f8f9fc', border: '1px solid #e8eaf0' }}>
            <Typography variant="body2" fontWeight={700} color="#1a1d23" gutterBottom>Compare Two Periods</Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={2}>
              Select two date ranges to overlay on the chart — max 24 hours per period. Same location filter applies.
            </Typography>
            {/* Active location filters */}
            <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
              <Typography variant="caption" fontWeight={600} color="text.secondary">Active filters:</Typography>
              {selectedState === 'all' && selectedSuburb === 'all' && selectedVenue === 'all' ? (
                <Alert severity="warning" sx={{ py: 0, px: 1.5, borderRadius: 1.5, '& .MuiAlert-message': { py: 0.5, fontSize: '0.75rem' } }}>
                  No location filter set — set a State, Suburb or Venue above for meaningful results
                </Alert>
              ) : (
                <>
                  {selectedState !== 'all' && <Chip label={`State: ${selectedState}`} size="small" sx={{ bgcolor: '#fff3f0', color: '#F24F2F', border: '1px solid #ffd0c8' }} />}
                  {selectedSuburb !== 'all' && <Chip label={`Suburb: ${selectedSuburb}`} size="small" sx={{ bgcolor: '#fff3f0', color: '#F24F2F', border: '1px solid #ffd0c8' }} />}
                  {selectedVenue !== 'all' && <Chip label={`Venue: ${selectedVenue}`} size="small" sx={{ bgcolor: '#fff3f0', color: '#F24F2F', border: '1px solid #ffd0c8' }} />}
                </>
              )}
            </Box>
            <Box display="flex" gap={3} flexWrap="wrap">
              <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#6c63ff', mb: 1, display: 'block' }}>PERIOD A</Typography>
                <Box display="flex" gap={1.5} alignItems="center">
                  <DatePicker label="From" value={compareStartA} onChange={setCompareStartA}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }} />
                  <DatePicker label="To" value={compareEndA} onChange={setCompareEndA}
                    minDate={compareStartA || undefined}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }} />
                </Box>
              </Box>
              <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#F24F2F', mb: 1, display: 'block' }}>PERIOD B</Typography>
                <Box display="flex" gap={1.5} alignItems="center">
                  <DatePicker label="From" value={compareStartB} onChange={setCompareStartB}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }} />
                  <DatePicker label="To" value={compareEndB} onChange={setCompareEndB}
                    minDate={compareStartB || undefined}
                    slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }} />
                </Box>
              </Box>
              <Box display="flex" alignItems="flex-end">
                <Button variant="contained" onClick={handleRunComparison}
                  disabled={!compareStartA || !compareEndA || !compareStartB || !compareEndB || compareLoading}
                  sx={{ height: 40, bgcolor: '#F24F2F', '&:hover': { bgcolor: '#d43f22' } }}>
                  {compareLoading ? 'Loading...' : 'Run Comparison'}
                </Button>
              </Box>
            </Box>
            {compareDataA.length > 0 && compareDataB.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                Period A: {compareDataA.length} points · Period B: {compareDataB.length} points
              </Typography>
            )}
          </Box>
        </LocalizationProvider>
      )}

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Summary Stat Cards */}
      <Grid container spacing={2.5} mb={4}>
        {[
          { label: 'Total Data Points', value: aggregateStats.total_entries, sub: `${aggregateStats.entries_with_both} with forecast & live`, icon: <Schedule />, accent: '#6c63ff', bgTint: '#f5f4ff' },
          {
            label: 'Forecast Accuracy', value: `${aggregateStats.avg_forecast_accuracy.toFixed(1)}%`,
            sub: aggregateStats.avg_forecast_accuracy > 80 ? 'Highly accurate' : 'Moderate accuracy',
            subColor: aggregateStats.avg_forecast_accuracy > 80 ? '#16a76a' : '#f59e0b',
            icon: <TrendingUp />, accent: '#16a76a', bgTint: '#f0faf5',
          },
          { label: 'Avg Forecasted', value: `${aggregateStats.avg_forecasted_busyness.toFixed(0)}%`, sub: 'Predicted busyness level', icon: <People />, accent: '#f59e0b', bgTint: '#fffbf0' },
          { label: 'Avg Live Busyness', value: `${aggregateStats.avg_live_busyness.toFixed(0)}%`, sub: 'Actual busyness level', icon: <People />, accent: '#e040fb', bgTint: '#fdf4ff' },
        ].map((card: any) => (
          <Grid key={card.label} size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{
              background: '#fff', borderRadius: 2.5, p: 2.5,
              border: '1px solid #e8eaf0', borderLeft: `4px solid ${card.accent}`,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.09)' },
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8a94a6', mb: 0.75 }}>{card.label}</Typography>
                  <Typography sx={{ fontSize: '1.9rem', fontWeight: 700, color: '#1a1d23', lineHeight: 1, mb: 0.5 }}>{card.value}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: card.subColor || '#8a94a6' }}>{card.sub}</Typography>
                </Box>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, background: card.bgTint, color: card.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 20 } }}>
                  {card.icon}
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Location Filters */}
      <Box mb={3} sx={{ p: 2.5, background: '#fff', borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8a94a6', mb: 1.5 }}>
          Filter by Location
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>State</InputLabel>
            <Select value={selectedState} label="State" onChange={handleStateChange} sx={{ fontSize: '0.8rem', borderRadius: 1.5 }}>
              <MenuItem value="all"><Box display="flex" alignItems="center" gap={1}><Typography variant="body2">All States</Typography><Chip label={states.length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} /></Box></MenuItem>
              {states.map(state => (
                <MenuItem key={state} value={state}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{state}</Typography>
                    <Chip label={venues.filter(v => v.state === state).length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Suburb</InputLabel>
            <Select value={selectedSuburb} label="Suburb" onChange={handleSuburbChange} disabled={selectedState === 'all' && suburbs.length === 0} sx={{ fontSize: '0.8rem', borderRadius: 1.5 }}>
              <MenuItem value="all"><Box display="flex" alignItems="center" gap={1}><Typography variant="body2">All Suburbs</Typography><Chip label={getAvailableSuburbs().length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} /></Box></MenuItem>
              {getAvailableSuburbs().map(suburb => (
                <MenuItem key={suburb} value={suburb}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{suburb}</Typography>
                    <Chip label={getAvailableVenues().filter(v => v.suburb === suburb).length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Venue</InputLabel>
            <Select value={selectedVenue} label="Venue" onChange={handleVenueChange} sx={{ fontSize: '0.8rem', borderRadius: 1.5 }}>
              <MenuItem value="all"><Box display="flex" alignItems="center" gap={1}><Typography variant="body2">All Venues</Typography><Chip label={getAvailableVenues().length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} /></Box></MenuItem>
              {getAvailableVenues().map(venue => {
                const vStats = venueStats.find(v => v.venue_name === venue.name);
                return (
                  <MenuItem key={venue.id} value={venue.name}>
                    <Box>
                      <Typography variant="body2">{venue.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {venue.suburb !== 'Unknown' ? `${venue.suburb} (${venue.postcode})` : venue.postcode}, {venue.state}
                        {vStats && ` · ${vStats.total_checks} checks`}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {(selectedState !== 'all' || selectedSuburb !== 'all' || selectedVenue !== 'all') && (
            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
              {selectedState !== 'all' && (
                <Chip label={`State: ${selectedState}`} onDelete={() => { setSelectedState('all'); setSelectedSuburb('all'); setSelectedVenue('all'); }} color="primary" size="small" sx={{ height: 24, fontSize: '0.72rem' }} />
              )}
              {selectedSuburb !== 'all' && (
                <Chip label={`Suburb: ${selectedSuburb}`} onDelete={() => { setSelectedSuburb('all'); setSelectedVenue('all'); }} color="primary" size="small" sx={{ height: 24, fontSize: '0.72rem' }} />
              )}
              {selectedVenue !== 'all' && (
                <Chip label={`Venue: ${selectedVenue}`} onDelete={() => setSelectedVenue('all')} color="primary" size="small" sx={{ height: 24, fontSize: '0.72rem' }} />
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Data limit warning — shown when unfiltered and at 1000 record cap */}
      {isAtDataLimit && selectedVenue === 'all' && selectedSuburb === 'all' && (
        <Alert
          severity="warning"
          sx={{ mb: 2, borderRadius: 2, border: '1px solid #fde68a' }}
          icon={<FilterList />}
        >
          <Typography variant="body2" fontWeight={600}>
            Showing the most recent 1,000 data points only
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            You have more historical data than can be displayed at once.
            Filter by <strong>suburb</strong> or <strong>venue</strong> above to see the full history for a specific location.
          </Typography>
        </Alert>
      )}

      {/* Main Chart */}
      <Card sx={{ mb: 3, borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Box>
              <Typography variant="h6" fontWeight={600} color="#1a1d23">Forecasted vs Live Capacity</Typography>
              <Typography variant="body2" color="text.secondary">Busyness levels across your venues</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Chip size="small" label={`${filteredData.filter(d => d.hasForecast).length} forecasts`} sx={{ height: 22, fontSize: '0.7rem', background: '#f5f4ff', color: '#6c63ff', border: '1px solid #e0dbff' }} />
              <Chip size="small" label={`${filteredData.filter(d => d.hasLive).length} live`} sx={{ height: 22, fontSize: '0.7rem', background: '#f0faf5', color: '#16a76a', border: '1px solid #bbf0d9' }} />
            </Box>
          </Box>

          {filteredData.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={380} flexDirection="column" gap={1.5}>
              <Info sx={{ fontSize: 40, color: '#d1d5e0' }} />
              <Typography variant="h6" color="text.secondary" fontWeight={500}>No data for selected range</Typography>
              <Typography variant="body2" color="text.secondary">Try a different time range or filter combination</Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              {renderChart()}
            </ResponsiveContainer>
          )}

          {chartType === 'scatter' && filteredData.length > 0 && (
            <Box mt={2} px={2} py={1.25} sx={{ background: '#f5f4ff', borderRadius: 1.5, border: '1px solid #e0dbff' }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#6c63ff', fontWeight: 500 }}>
                Points on the diagonal line indicate perfect forecast accuracy. Above the line means live busyness exceeded the forecast; below means it was lower.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Venue Forecast Performance Table — derived from filteredData so it respects filters */}
      {(() => {
        const filteredVenueMap = new Map<string, typeof filteredData>();
        filteredData.forEach(point => {
          if (!filteredVenueMap.has(point.venue_name)) filteredVenueMap.set(point.venue_name, []);
          filteredVenueMap.get(point.venue_name)!.push(point);
        });
        const filteredVenueStats = Array.from(filteredVenueMap.entries()).map(([venue_name, points]) => {
          const withBoth = points.filter(p => p.hasForecast && p.hasLive);
          const avgDelta = withBoth.length > 0 ? withBoth.reduce((s, p) => s + Math.abs(p.delta || 0), 0) / withBoth.length : 0;
          const accuracy = withBoth.length > 0 ? Math.max(0, 100 - (avgDelta / withBoth.length)) : 0;
          const forecastPts = points.filter(p => p.forecasted !== null);
          const livePts = points.filter(p => p.live !== null);
          return {
            venue_name,
            total_checks: points.length,
            avg_forecasted: forecastPts.length > 0 ? forecastPts.reduce((s, p) => s + (p.forecasted || 0), 0) / forecastPts.length : 0,
            avg_live: livePts.length > 0 ? livePts.reduce((s, p) => s + (p.live || 0), 0) / livePts.length : 0,
            accuracy_rate: accuracy,
          };
        }).sort((a, b) => b.total_checks - a.total_checks);

        return (
      <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6" fontWeight={600} color="#1a1d23">Venue Forecast Performance</Typography>
            <Typography variant="caption" color="text.secondary">{filteredVenueStats.length} venue{filteredVenueStats.length !== 1 ? 's' : ''} in scope</Typography>
          </Box>
          <Box display="flex" flexDirection="column" gap={1.25} mt={1}>
            {filteredVenueStats.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>No data for selected filters</Typography>
            ) : filteredVenueStats.map((venue) => (
              <Box key={venue.venue_name} sx={{ p: 2, borderRadius: 2, background: '#f8f9fc', border: '1px solid #eef0f5' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#1a1d23">{venue.venue_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{venue.total_checks} data points</Typography>
                  </Box>
                  <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
                    <Box textAlign="right">
                      <Typography sx={{ fontSize: '0.68rem', color: '#8a94a6' }}>Avg Forecast</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#6c63ff' }}>{venue.avg_forecasted.toFixed(0)}%</Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography sx={{ fontSize: '0.68rem', color: '#8a94a6' }}>Avg Live</Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#16a76a' }}>{venue.avg_live.toFixed(0)}%</Typography>
                    </Box>
                    <Box sx={{
                      px: 1.25, py: 0.5, borderRadius: 1.5,
                      background: venue.accuracy_rate > 80 ? '#f0faf5' : venue.accuracy_rate > 60 ? '#fffbf0' : '#fff5f5',
                      border: `1px solid ${venue.accuracy_rate > 80 ? '#bbf0d9' : venue.accuracy_rate > 60 ? '#fde68a' : '#fecaca'}`,
                    }}>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: venue.accuracy_rate > 80 ? '#16a76a' : venue.accuracy_rate > 60 ? '#f59e0b' : '#ef4444' }}>
                        {venue.accuracy_rate.toFixed(1)}% accurate
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
        );
      })()}
    </Box>
  );
};

export default BestTimeAnalytics;